import { motion } from "framer-motion";
import { Mail, ArrowRight } from "lucide-react";
import { CONTACT, IMAGES } from "@/data/content";

const HERO_IMG = IMAGES.heroWintergarten;

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[92vh] w-full overflow-hidden"
        >
            {/* Background image */}
            <div className="absolute inset-0">
                <img
                    src={HERO_IMG}
                    alt="Gemütliche Ferienwohnung Emsglück in Papenburg"
                    className="h-full w-full object-cover"
                    loading="eager"
                    fetchPriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/20 to-black/55" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />
            </div>

            {/* Content */}
            <div className="relative container-em min-h-[92vh] flex flex-col justify-end pb-16 md:pb-24 pt-32">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                        duration: 1.1,
                        ease: [0.21, 0.47, 0.32, 0.98],
                    }}
                    className="max-w-3xl"
                >
                    <span
                        className="inline-flex items-center gap-2 font-accent text-xs sm:text-sm uppercase tracking-[0.25em] text-white/85 mb-6"
                        data-testid="hero-eyebrow"
                    >
                        <span className="h-px w-8 bg-white/60" />
                        Ferienwohnung · Papenburg · Emsland
                    </span>

                    <h1
                        data-testid="hero-title"
                        className="font-display text-white text-5xl sm:text-6xl md:text-7xl leading-[1.02] tracking-tight text-balance"
                    >
                        Willkommen im
                        <br />
                        <em className="not-italic font-normal text-white/95">
                            Emsglück
                        </em>
                        <span className="text-primary-foreground/90">.</span>
                    </h1>

                    <p
                        data-testid="hero-subtitle"
                        className="mt-6 max-w-2xl text-white/85 text-lg sm:text-xl leading-relaxed"
                    >
                        Dein Zuhause auf Zeit in Papenburg – ideal für
                        Familien, Projektleiter und alle, die Komfort, Ruhe und
                        eine zentrale Lage schätzen.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <a
                            href="#anfrage"
                            data-testid="hero-inquiry-button"
                            className="group inline-flex items-center gap-2 rounded-full bg-white text-foreground px-7 py-4 text-base font-medium hover:bg-white/95 transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-black/10"
                        >
                            Verfügbarkeit anfragen
                            <ArrowRight
                                size={18}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </a>
                        <a
                            href={CONTACT.mailtoContact}
                            data-testid="hero-contact-button"
                            className="inline-flex items-center gap-2 rounded-full border border-white/50 text-white px-7 py-4 text-base font-medium hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
                        >
                            <Mail size={16} />
                            Jetzt Kontakt aufnehmen
                        </a>
                    </div>
                </motion.div>
            </div>

            {/* Subtle scroll cue */}
            <div
                aria-hidden
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/70"
            >
                <span className="font-accent text-[10px] tracking-[0.3em] uppercase">
                    Scrollen
                </span>
                <span className="h-10 w-px bg-white/50 animate-pulse" />
            </div>
        </section>
    );
}
